import { Check, ArrowRight, LogIn, Merge, UserPlus } from 'lucide-react';
import { maskPhone, maskIdCard } from './mask';
import type { RegisterSuccessData } from '../../../../api/register';
import type { RegisterFormData } from '../../../../stores/registerStore';

/**
 * Step 5: 注册完成（增强版）
 * - 脱敏信息核验摘要
 * - 流程摘要（升格/合并/订单迁移）
 * - 注册即登录：双令牌已注入，可直达首页
 */
export function Step5Success({
  result,
  formData,
  onHome,
  onLogin,
}: {
  result: RegisterSuccessData | null;
  formData: RegisterFormData;
  onHome: () => void;
  onLogin: () => void;
}) {
  const summary = result?.summary;

  const mergedCount = summary?.mergedCount ?? 0;
  const ordersMoved = summary?.ordersMoved ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <Check className="w-10 h-10 text-green-500" />
        </div>
        <h4 className="text-2xl font-bold text-slate-800">注册成功！</h4>
        <p className="text-slate-600 mt-2">
          {result?.message || '您已成功注册为系统用户'}
        </p>
        <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-teal-50 border border-teal-200 rounded-full">
          <Check className="w-4 h-4 text-teal-600" />
          <span className="text-sm text-teal-700 font-medium">已完成登录，可直接开始使用</span>
        </div>
      </div>

      {/* 流程摘要（升格/合并） */}
      {summary && (summary.upgradedFromCustomer || mergedCount > 0) && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h5 className="text-sm font-medium text-blue-700 mb-2">本次注册为您完成</h5>
          <ul className="text-sm text-blue-700 space-y-1.5">
            {summary.upgradedFromCustomer && (
              <li className="flex items-center gap-2">
                <UserPlus className="w-4 h-4 flex-shrink-0" />
                已关联系统内您的历史客户档案
              </li>
            )}
            {mergedCount > 0 && (
              <li className="flex items-center gap-2">
                <Merge className="w-4 h-4 flex-shrink-0" />
                自动合并 {mergedCount} 个重复档案
                {ordersMoved > 0 && `，${ordersMoved} 个订单已关联到您的账号`}
              </li>
            )}
          </ul>
        </div>
      )}

      {/* 信息核验摘要（脱敏） */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg divide-y divide-slate-100">
        <div className="p-4 flex justify-between text-sm">
          <span className="text-slate-500">用户名</span>
          <span className="text-slate-800 font-medium">{result?.username || formData.username}</span>
        </div>
        <div className="p-4 flex justify-between text-sm">

          <span className="text-slate-500">真实姓名</span>
          <span className="text-slate-800 font-medium">{result?.fullName || formData.fullName}</span>
        </div>
        <div className="p-4 flex justify-between text-sm">
          <span className="text-slate-500">联系电话</span>
          <span className="text-slate-800 font-medium">{maskPhone(result?.phone || formData.phone)}</span>
        </div>
        {(result?.backupPhone || formData.backupPhone) && (
          <div className="p-4 flex justify-between text-sm">
            <span className="text-slate-500">备用电话</span>
            <span className="text-slate-800 font-medium">{maskPhone(result?.backupPhone || formData.backupPhone)}</span>
          </div>
        )}
        <div className="p-4 flex justify-between text-sm">
          <span className="text-slate-500">身份证号</span>
          <span className="text-slate-800 font-medium">{maskIdCard(formData.idCard)}</span>
        </div>
        {(result?.email || formData.email) && (
          <div className="p-4 flex justify-between text-sm">
            <span className="text-slate-500">电子邮箱</span>
            <span className="text-slate-800 font-medium">{result?.email || formData.email}</span>
          </div>
        )}
      </div>

      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <h5 className="text-sm font-medium text-amber-700 mb-2">后续操作建议</h5>
        <ul className="text-sm text-amber-700 space-y-1">
          <li>• 登录系统后，可前往个人中心完善个人信息与称呼</li>
          <li>• 熟悉系统的订单添加、查询与对账操作流程</li>
          <li>• 如有任何问题，请联系客服寻求帮助</li>
        </ul>
      </div>

      {/* 导向按钮 */}
      <div className="flex gap-3">
        <button
          onClick={onHome}
          className="flex-1 py-3 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          进入首页
          <ArrowRight className="w-4 h-4" />
        </button>
        <button
          onClick={onLogin}
          className="px-6 py-3 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
        >
          <LogIn className="w-4 h-4" />
          去登录
        </button>
      </div>
    </div>
  );
}